import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

// The rail is the canvas's navigation, so the open document's outline has to reach it.
// The canvas has already loaded that document; making the rail fetch it again would put
// two readers on the same artifact and let them disagree while one of them is stale.
// The canvas publishes what it loaded instead, and the rail renders it. See ADR-167.
export type CanvasOutlineEntry = {
  ordinal: number
  title: string
  state: string
  // What opening this entry adds to the canvas URL. The canvas already scrolls its
  // focused anchor into view, so navigation is the whole mechanism. Absent when the
  // entry has no address: a slide with no selected version cannot be pointed at, and
  // offering a control that cannot go anywhere is worse than showing the row alone.
  position?: { section: number } | { slide: number; version: number }
}

export type CanvasOutline = {
  kind: 'proposal' | 'presentation'
  id: number
  entries: Array<CanvasOutlineEntry>
  // The entry the canvas is currently focused on, which the rail marks as current.
  activeOrdinal?: number
}

type CanvasOutlineStore = {
  outline?: CanvasOutline
  publish: (outline: CanvasOutline | undefined) => void
}

const CanvasOutlineContext = createContext<CanvasOutlineStore | undefined>(undefined)

export function CanvasOutlineProvider({ children }: { children: ReactNode }) {
  const [outline, setOutline] = useState<CanvasOutline>()
  const publish = useCallback((next: CanvasOutline | undefined) => {
    setOutline((current) => (sameOutline(current, next) ? current : next))
  }, [])
  const value = useMemo<CanvasOutlineStore>(
    () => ({ ...(outline === undefined ? {} : { outline }), publish }),
    [outline, publish]
  )
  return <CanvasOutlineContext.Provider value={value}>{children}</CanvasOutlineContext.Provider>
}

// Publishing runs from an effect on every canvas render, so an unchanged outline must
// not produce a new object: the rail would re-render on every keystroke in the chat.
function sameOutline(current: CanvasOutline | undefined, next: CanvasOutline | undefined): boolean {
  if (current === undefined || next === undefined) return current === next
  if (current.kind !== next.kind || current.id !== next.id) return false
  if (current.activeOrdinal !== next.activeOrdinal) return false
  if (current.entries.length !== next.entries.length) return false
  return current.entries.every((entry, index) => {
    const candidate = next.entries[index]
    return (
      candidate !== undefined &&
      entry.ordinal === candidate.ordinal &&
      entry.title === candidate.title &&
      entry.state === candidate.state
    )
  })
}

// Outside a provider the canvas simply has nowhere to publish, which is the case in
// component tests that render one surface on its own. That is not an error.
export function useCanvasOutline(): CanvasOutlineStore {
  return useContext(CanvasOutlineContext) ?? noOutlineStore
}

const noOutlineStore: CanvasOutlineStore = { publish: () => undefined }
